<template>
  <div
    class="VisionSelect"
    :class="[
      {
        'VisionSelect--disabled': disabled,
      },
      `VisionSelect--shape-${shape}`
    ]"
  >
    <div
      v-if="label || labelHint"
      class="VisionSelect__label-wrapper"
    >
      <label
        :for="id"
        class="VisionSelect__label"
      >
        {{ label }}
      </label>
      <span
        v-if="labelHint"
        class="VisionSelect__label-hint"
      >
        {{ labelHint }}
      </span>
    </div>
    <div class="VisionSelect__wrapper">
      <select
        :id="id"
        :disabled="disabled"
        :value="modelValue"
        @change="$emit('update:modelValue', $event.target.value)"
      >
        <option
          v-for="option in options"
          :key="option.value"
          :value="option.value"
          :disabled="option.disabled"
          :selected="option.selected"
        >
          {{ option.displayValue }}
        </option>
      </select>
      <div class="VisionSelect__icon">
        <VisionIcon
          icon="chevron"
          size="xx-small"
          colour="grey-dark"
          :rotate="90"
        />
      </div>
    </div>
    <p
      v-if="error"
      :id="ariaDescribedBy"
      class="VisionSelect__error-msg"
    >
      {{ error }}
    </p>
    <p
      v-if="hint && !error"
      :id="ariaDescribedBy"
      class="VisionSelect__hint"
    >
      {{ hint }}
    </p>
  </div>
</template>

<script>
import VisionIcon from '../VisionIcon/VisionIcon.vue'

export default {
  name: 'VisionSelect',

  components: {
    VisionIcon,
  },

  props: {
    id: {
      type: String,
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      required: false,
      default: '',
    },
    error: {
      type: String,
      default: '',
      required: false,
    },
    hint: {
      type: String,
      default: '',
      required: false,
    },
    labelHint: {
      type: String,
      default: '',
      required: false,
    },
    shape: {
      type: String,
      default: 'square',
      validator: (value) => {
        return ['rounded', 'square'].includes(value)
      },
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    options: {
      type: Array,
      required: true,
    },
    modelValue: {
      type: String,
      required: true,
    }
  },

  emits: ['update:modelValue'],

  computed: {
    calculatedHint() {
      if (this.showOptions) {
        return ''
      }

      return this.hint
    },
  },
}
</script>

<style lang="postcss">
.VisionSelect__label-wrapper {
  @apply flex justify-between mb-1;
}

.VisionSelect__label {
  @apply block text-sm font-medium text-off-black;
}

.VisionSelect__label-hint {
  @apply text-xxs text-gray-500;
}

.VisionSelect__wrapper {
  @apply relative;

  select {
    @apply appearance-none flex-1 block
      w-full
      bg-grey-light
      p-xxs pr-10
      border-[1px]
      border-grey-light
      focus:outline-none
      focus-within:border-grey
      sm:text-sm;
  }
}

.VisionSelect__icon {
  @apply absolute top-1/2 right-3 -translate-y-1/2;
}

.VisionSelect__error-msg {
  @apply text-red;
}

.VisionSelect__hint {
  @apply text-gray-500;
}

.VisionSelect__error-msg,
.VisionSelect__hint {
  @apply mt-2 text-xxs;
}

/* STATES */

.VisionSelect--disabled {
  .VisionSelect__label-wrapper,
  .VisionSelect__hint,
  .VisionSelect__icon {
    @apply opacity-40;
  }
}

/* SHAPES */

.VisionSelect--shape-rounded {
  .VisionSelect__label-wrapper,
  .VisionSelect__error-msg,
  .VisionSelect__hint {
    @apply px-xs;
  }

  .VisionSelect__wrapper select {
    @apply rounded-full p-xs pr-12;
  }

  .VisionSelect__icon {
    @apply pr-[0.8rem];
  }
}

.VisionSelect--shape-square {
  .VisionSelect__input {
    @apply rounded-md;
  }
}
</style>
